'use client';
import MuiTypography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import idsTokensDefault from '../../utils/tokens/idsTokens';
import withRef from '../../utils/withRef';

const variantMap = {
  title1: 'h3',
  title2: 'h4',
  bodySmall: 'body2',
  bodyDefault: 'body1',
  subtitle: 'h4',
  title: 'h3',
  subheadline: 'h2',
  headline: 'h1',
};

export const fixVariant = (variant) => variantMap[variant] || variant;

const variantToIconSizeMap = {
  caption: 'extraSmall',
  bodySmall: 'extraSmall',
  bodyDefault: 'small',
  subtitle: 'small',
  title: 'medium',
  subheadline: 'large',
  headline: 'large',
};

export const getIconSize = (variant) => variantToIconSizeMap[variant] || 'small';

export const styles = (
  { idsTokens = idsTokensDefault },
  { actualIconSize = 'small', variant = 'bodyDefault' }
) => ({
  caption: {
    color: idsTokens.typography.default.color.default.neutral,
    lineHeight: idsTokens.typography.default.sizing.icon.lineHeight.caption,
    '& svg': {
      marginRight: idsTokens.typography.default.spacing.gap.caption,
    },
  },
  bodySmall: {
    color: idsTokens.typography.default.color.default.neutral,
    fontSize: idsTokens.typography.font.size.sm,
    lineHeight: idsTokens.typography.lineHeight.sm,
    letterSpacing: idsTokens.typography.spacing.letter.md,
    fontWeight: idsTokens.typography.font.weight.regular,
    '& svg': {
      marginRight: idsTokens.typography.default.spacing.gap.bodySmall,
    },
  },
  bodyDefault: {
    color: idsTokens.typography.default.color.default.neutral,
    fontSize: idsTokens.typography.font.size.base,
    lineHeight: idsTokens.typography.lineHeight.base,
    letterSpacing: idsTokens.typography.spacing.letter.lg,
    fontWeight: idsTokens.typography.font.weight.regular,
    '& svg': {
      marginRight: idsTokens.typography.default.spacing.gap.bodyDefault,
    },
  },
  subtitle: {
    color: idsTokens.typography.default.color.default.neutral,
    fontSize: idsTokens.typography.font.size.xl,
    lineHeight: idsTokens.typography.lineHeight.xl,
    letterSpacing: idsTokens.typography.spacing.letter.xl,
    fontWeight: idsTokens.typography.font.weight.regular,
    '& svg': {
      marginRight: idsTokens.typography.default.spacing.gap.subtitle,
    },
  },
  title: {
    color: idsTokens.typography.default.color.default.neutral,
    fontSize: idsTokens.typography.font.size['2x'],
    lineHeight: idsTokens.typography.lineHeight['3x'],
    letterSpacing: idsTokens.typography.spacing.letter['2x'],
    fontWeight: idsTokens.typography.font.weight.regular,
    '& svg': {
      marginRight: idsTokens.typography.default.spacing.gap.title,
    },
  },
  subheadline: {
    color: idsTokens.typography.default.color.default.neutral,
    fontSize: idsTokens.typography.font.size['4x'],
    lineHeight: idsTokens.typography.lineHeight['2x'],
    letterSpacing: idsTokens.typography.spacing.letter.none,
    fontWeight: idsTokens.typography.font.weight.regular,
    '& svg': {
      marginRight: idsTokens.typography.default.spacing.gap.subheadline,
    },
  },
  headline: {
    color: idsTokens.typography.default.color.default.neutral,
    fontSize: idsTokens.typography.font.size['7x'],
    lineHeight: idsTokens.typography.lineHeight['6x'],
    letterSpacing: idsTokens.typography.spacing.letter.negative.lg,
    fontWeight: idsTokens.typography.font.weight.regular,
    '& svg': {
      marginRight: idsTokens.typography.default.spacing.gap.headline,
    },
  },
  lowEmphasis: {
    fontWeight: idsTokens.typography.font.weight.medium,
  },
  highEmphasis: {
    fontWeight: idsTokens.typography.font.weight.semibold,
  },
  higherEmphasis: {
    fontWeight: idsTokens.typography.font.weight.bold,
  },
  accent: {
    color: idsTokens.typography.default.color.default.accent,
  },
  brand: {
    color: idsTokens.typography.default.color.default.brand,
  },
  decorative1: {
    color: idsTokens.typography.default.color.default.decorative1,
  },
  decorative2: {
    color: idsTokens.typography.default.color.default.decorative2,
  },
  decorative3: {
    color: idsTokens.typography.default.color.default.decorative3,
  },
  decorative4: {
    color: idsTokens.typography.default.color.default.decorative4,
  },
  decorative5: {
    color: idsTokens.typography.default.color.default.decorative5,
  },
  error: {
    color: idsTokens.typography.default.color.default.danger,
  },
  neutral: {
    color: idsTokens.typography.default.color.default.neutral,
  },
  success: {
    color: idsTokens.typography.default.color.default.success,
  },
  // Inverse Modes
  accentInverse: {
    color: idsTokens.typography.default.color.inverse.accent,
  },
  brandInverse: {
    color: idsTokens.typography.default.color.inverse.brand,
  },
  decorative1Inverse: {
    color: idsTokens.typography.default.color.inverse.decorative1,
  },
  decorative2Inverse: {
    color: idsTokens.typography.default.color.inverse.decorative2,
  },
  decorative3Inverse: {
    color: idsTokens.typography.default.color.inverse.decorative3,
  },
  decorative4Inverse: {
    color: idsTokens.typography.default.color.inverse.decorative4,
  },
  decorative5Inverse: {
    color: idsTokens.typography.default.color.inverse.decorative5,
  },
  errorInverse: {
    color: idsTokens.typography.default.color.inverse.danger,
  },
  neutralInverse: {
    color: idsTokens.typography.default.color.inverse.neutral,
  },
  successInverse: {
    color: idsTokens.typography.default.color.inverse.success,
  },
  icon: {
    verticalAlign: 'middle',
  },
  withIconIndent: (function({ actualIconSize, variant }) {
    const sizeMap = {
      extraSmall: '17px',
      small: '20px',
      medium: '24px',
      large: '35px',
    };

    return {
      paddingLeft: `calc(${idsTokens.typography.default.spacing.gap[variant]} + ${sizeMap[actualIconSize]})`,
      textIndent: `calc((${idsTokens.typography.default.spacing.gap[variant]} + ${sizeMap[actualIconSize]}) * -1)`,
    };
  })({ actualIconSize, variant }),
  darkMode: {
    color: idsTokens.typography.default.color.inverse.neutral,
  },
});

const useStyles = makeStyles()(styles);

export const Typography = ({
  darkMode,
  variant,
  className,
  emphasis = 'default',
  mode,
  inverse,
  iconMode,
  iconSize,
  LeadingIcon,
  noWrap,
  children,
  forwardedRef: ref,
  ...rest
}) => {
  const modeClass = inverse ? `${mode}Inverse` : mode;
  const fixedVariant = fixVariant(variant);
  const actualIconSize = iconSize ?? getIconSize(variant);

  const { classes, cx } = useStyles({ actualIconSize, variant });

  return (
    <MuiTypography
      className={cx(
        classes[variant],
        darkMode && classes.darkMode,
        classes[`${emphasis}Emphasis`],
        ['title', 'subheadline', 'headline'].includes(variant) &&
          emphasis === 'high' &&
          classes.higherEmphasis,
        classes[modeClass],
        LeadingIcon && variant !== 'caption' && !noWrap && classes.withIconIndent,
        className
      )}
      variant={fixedVariant}
      noWrap={noWrap}
      {...rest}
      ref={ref}
    >
      {LeadingIcon && (
        <LeadingIcon className={cx(classes.icon, classes[iconMode])} fontSize={actualIconSize} />
      )}
      {children}
    </MuiTypography>
  );
};

Typography.propTypes = {
  /** If `true`, the component has a lighter appearance, for display on a dark background. */
  darkMode: PropTypes.bool,
  /** The font weight. */
  emphasis: PropTypes.oneOf(['default', 'low', 'high', 'higher']),
  /** If `true`, the text has a bottom margin added. */
  gutterBottom: PropTypes.bool,
  /** The color of the icon. */
  iconMode: PropTypes.oneOf([
    'accent',
    'brand',
    'decorative1',
    'decorative2',
    'decorative3',
    'decorative4',
    'decorative5',
    'error',
    'neutral',
    'success',
  ]),
  /** The size of the icon. */
  iconSize: PropTypes.oneOf(['extraSmall', 'small', 'medium', 'large']),
  /** If `true`, the color will be inverted for usage on an inverted background. */
  inverse: PropTypes.bool,
  /** An optional icon to display before the text. */
  LeadingIcon: PropTypes.elementType,
  /** The color of the text and optional icon. */
  mode: PropTypes.oneOf([
    'accent',
    'brand',
    'decorative1',
    'decorative2',
    'decorative3',
    'decorative4',
    'decorative5',
    'error',
    'neutral',
    'success',
  ]),
  /**
   * If `true`, the text truncates with an overflow ellipsis.
   * Note that text overflow can only happen with block or inline-block level
   * elements (the element needs to have a width in order to overflow).
   */
  noWrap: PropTypes.bool,
  /** Applies the theme typography styles. */
  variant: PropTypes.oneOf([
    'headline',
    'subheadline',
    'title',
    'subtitle',
    'bodyDefault',
    'bodySmall',
    'caption',
  ]),
};

export default withRef()(Typography);
